/**
 * HelcimPay.js hand-off: the parts of the portal payment flow that decide
 * WHO we are listening to, WHAT Helcim said, WHICH attempt it belongs to, and
 * WHEN a payment window counts as dead.
 *
 * Pure and DOM-light so PortalBilling can use them and tests can drive them
 * without mounting the page. Every fact here was read from Helcim's own
 * start.js (secure.helcim.app/helcim-pay/services/start.js) and
 * devdocs.helcim.com/docs/render-helcimpayjs on 2026-09-08/09:
 *
 *   * appendHelcimPayIframe(token) appends <iframe id="helcimPayIframe"
 *     src="https://secure.helcim.app/helcim-pay/<token>…"> and RETURNS it.
 *   * The widget posts window messages with eventName
 *     "helcim-pay-js-<checkoutToken>" and eventStatus SUCCESS | ABORTED | HIDE;
 *     the transaction lives in eventMessage as JSON (data.data). There is no
 *     "ready" or "initialised" message.
 *   * Those messages come from origin https://secure.helcim.app. The portal's
 *     old gate (`origin.includes('helcim.com')`) dropped every one of them,
 *     so the page never heard a success, a decline or a close, and its
 *     20-second watchdog fired on every widget that opened — tearing down live
 *     payment windows and telling customers nothing was charged.
 */

// 'all' is what the portal mints: Helcim cc-ach, both rails on one page.
// 'card' and 'ach' are single-rail sessions — Helcim rejects those when the
// Fee Saver fee rides along, so the portal cannot produce one any more
// (quickbooks_views.portal_invoice_pay pins the rail server-side). They stay
// in the union because refs and records predating 2026-09-10 carry them, and
// because the vault/verify path still legitimately asks for card only.
//
// This member exists so an attempt object and its telemetry can both say what
// was really minted instead of one of them lying. It changes no behaviour:
// transactionIsAch below short-circuits only on 'ach', so 'all' and 'card'
// take the identical "decide from the payload" branch.
export type PaymentRail = 'card' | 'ach' | 'all';
export type AttemptOutcome = 'started' | 'failed' | 'abandoned';

// ── Trust boundary ──────────────────────────────────────────────────────
// Exact origins, compared with equality. A substring test is both too loose
// (helcim.com.attacker.io) and, as it turned out, too tight (secure.helcim.app).
export const HELCIM_MESSAGE_ORIGINS: ReadonlySet<string> = new Set([
  'https://secure.helcim.app',
  'https://api.helcim.com',
]);

export function isHelcimMessageOrigin(origin: string): boolean {
  return HELCIM_MESSAGE_ORIGINS.has(origin);
}

// ── What Helcim said ────────────────────────────────────────────────────
export type HelcimEventStatus = 'SUCCESS' | 'ABORTED' | 'HIDE';

export interface ParsedHelcimMessage {
  eventName: string;
  status: HelcimEventStatus | null;
  /** Top-level message fields merged with the parsed eventMessage transaction. */
  payload: Record<string, unknown>;
}

/**
 * Parse a HelcimPay.js window message. Returns null for anything that is not
 * a HelcimPay message (wrong shape, or an eventName that is not
 * "helcim-pay-js-…"). `status` is null for an eventStatus we do not know.
 */
export function parseHelcimMessage(data: unknown): ParsedHelcimMessage | null {
  if (!data || typeof data !== 'object') return null;
  const d = data as Record<string, unknown>;
  const eventName = typeof d.eventName === 'string' ? d.eventName : '';
  if (!eventName.includes('helcim-pay-js')) return null;

  const rawStatus = String(d.eventStatus ?? '').toUpperCase();
  const status: HelcimEventStatus | null =
    rawStatus === 'SUCCESS' || rawStatus === 'ABORTED' || rawStatus === 'HIDE' ? rawStatus : null;

  let payload: Record<string, unknown> = { ...d };
  if (typeof d.eventMessage === 'string') {
    try {
      const parsed = JSON.parse(d.eventMessage);
      const txn = parsed?.data?.data ?? parsed?.data ?? parsed;
      if (txn && typeof txn === 'object') payload = { ...payload, ...txn };
    } catch {
      // Not JSON (Helcim sends a plain string on some errors). Keep the top level.
    }
  } else if (d.eventMessage && typeof d.eventMessage === 'object') {
    const em = d.eventMessage as Record<string, any>;
    const txn = em?.data?.data ?? em?.data ?? em;
    if (txn && typeof txn === 'object') payload = { ...payload, ...txn };
  }
  return { eventName, status, payload };
}

/** Best-effort rail of a completed transaction.
 *
 *  `rail` is what the portal asked Helcim to OFFER. Since dbc80efa that is
 *  always 'all', so the payload decides every time; the 'ach' short-circuit
 *  now only fires for a legacy ach-only session.
 *
 *  WHICH PAYLOAD FIELD HELCIM ACTUALLY SENDS IS STILL UNCONFIRMED, and the
 *  checks below are deliberately broad because of it. The raw postMessage
 *  never reaches the server: portal_record_helcim_payment receives only
 *  { transactionId, amount, type, cardType }, and `type` is the
 *  ALREADY-CLASSIFIED output of this function. Nothing we store keeps the
 *  original payload, so production can only ever confirm the OUTCOME.
 *
 *  The one confirmed both-rails ACH payment — Nate Meagher, invoice 123,
 *  Helcim transaction 631622, $1,198.96, 2026-09-10 16:50-16:53 UTC; minted
 *  method=all -> cc-ach with fee=1; the customer chose bank inside the widget
 *  — produced exactly this server line, on the first ever production run of
 *  record-helcim-payment:
 *
 *      Recorded Helcim payment 631622 for invoice 123: $1198.96 via ACH
 *
 *  and Helcim's dashboard shows it as ACH/EFT, Batch 50, status Processing.
 *  So this function returned true for it, which means `cardType` was absent
 *  and at least one of achName / bankAccountId / bankToken / type was set.
 *  WHICH ONE is not recoverable from anything we keep. Do not narrow these
 *  checks to a single field until a real payload is captured in the browser.
 */
export function transactionIsAch(rail: PaymentRail, payload: Record<string, unknown>): boolean {
  if (rail === 'ach') return true;
  if (payload.cardType) return false;
  const type = String(payload.type ?? payload.paymentType ?? '').toLowerCase();
  return Boolean(payload.achName || payload.bankAccountId || payload.bankToken || type === 'ach'
    || type.includes('withdrawal'));
}

// ── Which attempt a record belongs to ───────────────────────────────────
/**
 * One customer attempt to pay: created only after a checkout was minted, and
 * carried by closure into every later event (watchdog, Helcim message,
 * telemetry). A later click creates a NEW attempt; it can never rewrite this
 * one. (The previous design kept the rail in one shared ref that every click
 * overwrote, so a record posted 20 seconds later described the latest click
 * — which is how two card attempts were recorded as method=ach.)
 */
export interface PaymentAttempt {
  readonly invoiceId: number;
  readonly rail: PaymentRail;
  readonly amount?: string;
  readonly token: string;
  readonly startedAt: number;
  /** Set once the Helcim iframe's load event fired. */
  loaded: boolean;
}

export function createAttempt(
  fields: { invoiceId: number; rail: PaymentRail; amount?: string; token: string },
  now: number = Date.now(),
): PaymentAttempt {
  return { ...fields, startedAt: now, loaded: false };
}

export interface AttemptRecordBody {
  outcome: AttemptOutcome;
  reason?: string;
  detail?: string;
  amount?: string;
  payment_method: PaymentRail;
}

/** The telemetry body for THIS attempt. The rail comes from the attempt,
 *  never from page state. */
export function attemptRecordBody(
  attempt: Pick<PaymentAttempt, 'rail' | 'amount'>,
  outcome: AttemptOutcome,
  reason?: string,
  detail?: string,
): AttemptRecordBody {
  return { outcome, reason, detail, amount: attempt.amount, payment_method: attempt.rail };
}

// ── When a payment window counts as dead ────────────────────────────────
/**
 * Arm a "did the widget ever appear" watchdog on the iframe HelcimPay.js
 * appended. The iframe's `load` event is the only signal Helcim gives that
 * its document arrived (it fires for a cross-origin frame; it does NOT say
 * the customer did anything). So:
 *
 *   * load fires  → onLoaded, timer cancelled. The customer may now take as
 *                   long as they like. Nothing here will ever tear a loaded
 *                   widget down.
 *   * no load by timeoutMs → onNeverLoaded. That, and only that, is a widget
 *                   that failed to open.
 *
 * Returns a cancel function (idempotent). If `iframe` is null the timer still
 * runs, because "no iframe" and "iframe that never loads" are the same failure
 * from the customer's chair.
 */
export function armLoadWatchdog(
  iframe: HTMLIFrameElement | null,
  timeoutMs: number,
  handlers: { onLoaded: () => void; onNeverLoaded: () => void },
): () => void {
  let done = false;
  let timer: ReturnType<typeof setTimeout> | null = null;

  const onLoad = () => {
    if (done) return;
    done = true;
    if (timer) clearTimeout(timer);
    timer = null;
    handlers.onLoaded();
  };

  timer = setTimeout(() => {
    if (done) return;
    done = true;
    timer = null;
    iframe?.removeEventListener('load', onLoad);
    handlers.onNeverLoaded();
  }, timeoutMs);

  iframe?.addEventListener('load', onLoad, { once: true });

  return () => {
    if (done) return;
    done = true;
    if (timer) clearTimeout(timer);
    timer = null;
    iframe?.removeEventListener('load', onLoad);
  };
}

/** The iframe HelcimPay.js appended for this session, whether or not the
 *  (typed-as-void, actually element-returning) append call handed it back. */
export function resolveHelcimIframe(appended: unknown): HTMLIFrameElement | null {
  if (typeof HTMLIFrameElement !== 'undefined' && appended instanceof HTMLIFrameElement) return appended;
  const byId = document.getElementById('helcimPayIframe');
  return byId instanceof HTMLIFrameElement ? byId : null;
}
